import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import UltimatePagination from 'react-ultimate-pagination-bootstrap-3';
import { Panel } from 'react-bootstrap';
import { orderBy, get } from 'lodash';

import { isBlank } from 'helpers/string_helpers';
import LoadingSpinner from 'components/loading_spinner';
import EmptyState from 'components/project/empty_state';
import ContactRolePropTypes from 'bundles/project/prop_types/contact_roles';
import ContactSelector from 'components/project/contact_selector';
import CompaniesPageHeader from './companies_page_header';
import CompaniesFilterTopbar from './companies_filter_topbar';
import LoadErrorMessage from 'components/project/load_error_message';
import CompaniesTable from './companies_table';
import { NAME, COLUMN_DEFINITIONS, CONFIGURABLE_COLUMNS } from './company_cell';

import '../styles.scss';

import { addContact, removeContact, updateContactRole } from 'components/project/actions';
import { retrieveCompanies } from '../../../actions/contacts';
import { setCurrentCompanyColumns } from '../../../actions/session';
import { getProject } from '../../../selectors';
import {
  getCompanies,
  getLoadError,
  getCompanyColumns,
  isLoading
} from '../../../selectors/contacts';

export const RESULTS_PER_PAGE = 12;

export function CompaniesTab({
  availableRelationships,
  companies,
  currentColumns,
  isLoading,
  loadError,
  project,
  retrieveRecords,
  setColumns,
  onAddCompany,
  onRemoveCompany,
  onUpdateCompany
}) {
  const [filters, setFilters] = useState({ searchText: '', relationshipFilter: null });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ field: NAME, reverse: true });
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState();

  useEffect(() => {
    retrieveRecords();
  }, [project]);

  const showCompanyEditModal = (company = null) => {
    setShowCompanyModal(true);
    setSelectedCompany(company);
  };

  const hideCompanyEditModal = () => {
    setShowCompanyModal(false);
    setSelectedCompany(null);
  };

  const handleFilterChange = filter => setFilters({ ...filters, ...filter });
  const changeSort = (fieldClicked) => {
    const reverse = sort.field === fieldClicked ? !sort.reverse : false;
    setSort({ field: fieldClicked, reverse });
  };

  const sortedContacts = () => {
    const { sortValue } = COLUMN_DEFINITIONS[sort.field];
    const direction = sort.reverse ? 'desc' : 'asc';
    return orderBy(companies, [company => get(company, sortValue)], [direction]);
  };

  const searchFilter = (companies) => {
    const filterText = (filters.searchText || '').toLowerCase();

    return filterText.length > 0
      ? companies.filter(company => get(company, 'contactName').toLowerCase().includes(filterText))
      : companies;
  };

  const relationshipOptions = () => [{ id: '', label: 'All Relationships', value: '' }, ...availableRelationships];
  const currentRelationship = () => availableRelationships.find(cr => cr.id === filters.relationshipFilter);

  const relationshipSearch = (relationship) =>  {
    if (relationship) setPage(1);

    handleFilterChange({ relationshipFilter: isBlank(relationship) ? null : relationship.id });
  };

  const relationshipFilter = (companies) => {
    const relationship = currentRelationship();

    return relationship ? companies.filter(company => company.relationship === relationship.id) : companies;
  };

  const shownCompanies = () => {
    let companies = sortedContacts();
    companies = searchFilter(companies);
    companies = relationshipFilter(companies);
    return companies;
  };

  const handleListChange = () => {
    hideCompanyEditModal();
    retrieveRecords();
  };

  const addNewCompany = company => (
    onAddCompany(
      project.id,
      company.associatedModelId,
      company.associatedModelType,
      company.relation
    ).then(handleListChange)
  );

  const updateCompany = company => (
    onUpdateCompany(
      project.id,
      selectedCompany.id,
      company.relation
    ).then(handleListChange)
  );

  const currentPage = () => Math.min(page, totalPages());
  const totalPages = () => Math.ceil(shownCompanies().length / RESULTS_PER_PAGE);

  const currentCompanies = () => {
    const start = (currentPage() - 1) * RESULTS_PER_PAGE;
    const end = start + RESULTS_PER_PAGE;
    return shownCompanies().slice(start, end);
  };

  const searchCompanies = (e) =>  {
    if (e.target.value) setPage(1);

    handleFilterChange({ searchText: (e.target.value || '') });
  };

  const removeCompany = company => (
    onRemoveCompany(
      project.id,
      company.id
    ).then(() => retrieveRecords())
  );

  const renderEmptyState = () => (
    <EmptyState
      imageSrc="/images/project_empty_states/companies.svg"
      message={companies.length === 0 ? 'No companies to display.' : 'No companies match the current search criteria.'}
      subMessage={companies.length === 0 ? 'Get started by clicking on the “New Company” button.' : null}
    />
  );

  const renderTable = () => {
    return (
      <div className="border">
        <CompaniesTable
          columns={currentColumns || CONFIGURABLE_COLUMNS}
          companies={currentCompanies()}
          defaultColumns={CONFIGURABLE_COLUMNS}
          sortColumn={sort.field}
          sortDesc={sort.reverse}
          stickyColumns={1}
          onRemoveCompany={removeCompany}
          onSetColumns={setColumns}
          onSort={changeSort}
          onUpdateCompany={showCompanyEditModal}
        />
      </div>
    );
  };

  const renderPagination = () => {
    if (isLoading || totalPages() <= 1)  {
      return <div className="py-5"/>;
    }

    return (
      <div className="text-center">
        <UltimatePagination
          currentPage={currentPage()}
          totalPages={totalPages()}
          onChange={setPage}
        />
      </div>
    );
  };

  const renderBody = () => {
    let content;

    if (isLoading) {
      content = <LoadingSpinner addBottomBuffer />;
    } else if (shownCompanies().length === 0) {
      content = renderEmptyState();
    } else {
      content = renderTable();
    }

    return (
      <Panel>
        <Panel.Body className="p-0">
          {content}
        </Panel.Body>
      </Panel>
    );
  };

  return (
    <React.Fragment>
      <CompaniesPageHeader onAddCompany={showCompanyEditModal} />
      <LoadErrorMessage loadError={loadError}/>
      <CompaniesFilterTopbar
        companyCount={currentCompanies().length}
        currentPage={currentPage()}
        isLoading={isLoading}
        pageSize={RESULTS_PER_PAGE}
        relationshipFilter={currentRelationship()}
        relationshipOptions={relationshipOptions()}
        searchText={filters.searchText}
        totalCount={shownCompanies().length}
        onRelationshipChange={relationshipSearch}
        onSearchChange={searchCompanies}
      />
      {renderBody()}
      {renderPagination()}
      <ContactSelector
        personOrCompany="company"
        selectedContactRole={selectedCompany}
        show={showCompanyModal}
        onCancel={hideCompanyEditModal}
        onSave={selectedCompany ? updateCompany : addNewCompany}
      />
    </React.Fragment>
  );
}

CompaniesTab.propTypes = {
  availableRelationships: ContactRolePropTypes,
  companies: PropTypes.array.isRequired,
  currentColumns: PropTypes.arrayOf(PropTypes.string),
  isLoading: PropTypes.bool,
  loadError: PropTypes.string,
  project: PropTypes.object.isRequired,
  retrieveRecords: PropTypes.func.isRequired,
  setColumns: PropTypes.func.isRequired,
  onAddCompany: PropTypes.func.isRequired,
  onRemoveCompany: PropTypes.func.isRequired,
  onUpdateCompany: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  availableRelationships: state.contactRoles,
  companies: getCompanies(state),
  currentColumns: getCompanyColumns(state),
  isLoading: isLoading(state),
  loadError: getLoadError(state),
  project: getProject(state)
});

const mapDispatchToProps = {
  onAddCompany: addContact,
  onRemoveCompany: removeContact,
  onUpdateCompany: updateContactRole,
  retrieveRecords: retrieveCompanies,
  setColumns: setCurrentCompanyColumns  
};

export default connect(mapStateToProps, mapDispatchToProps)(CompaniesTab);
